import {
  ActionIcon,
  Paper,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { Edit2, Plus, Trash2, X as XIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './page.module.css';

import { pointsApi, type PointNote } from '@fields/data-access';
import { FullMainHeight } from '@ui';

export function PointNotesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: '/map-notes' });
  const search = useSearch({ from: '/_app/map-notes' });
  const queryClient = useQueryClient();

  const notesQuery = useQuery({
    queryKey: ['points', search.pointId, 'notes'],
    queryFn: () => pointsApi.getPointNotes(search.pointId),
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: number) => pointsApi.deletePointNote(search.pointId, noteId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['points', search.pointId, 'notes'],
      });
    },
  });

  const sortedNotes = useMemo(
    () =>
      [...(notesQuery.data ?? [])].sort(
        (a, b) => dayjs(b.updated_at).valueOf() - dayjs(a.updated_at).valueOf(),
      ),
    [notesQuery.data],
  );

  const handleClose = () => {
    navigate({
      to: '/map',
      search: {
        ilotId: search.ilotId,
        pointId: search.pointId,
        pointCode: search.pointCode,
        sampleId: search.sampleId,
        lng: search.lng,
        lat: search.lat,
        zoom: search.zoom,
        scope: search.scope,
      },
    });
  };

  const handleCreateNote = () => {
    navigate({
      to: '/map-note-create',
      search: {
        ilotId: search.ilotId,
        pointId: search.pointId,
        pointCode: search.pointCode,
        sampleId: search.sampleId,
        lng: search.lng,
        lat: search.lat,
        zoom: search.zoom,
        scope: search.scope,
      },
    });
  };

  const openDetail = (note: PointNote) => {
    navigate({
      to: '/map-note',
      search: {
        ilotId: search.ilotId,
        pointId: search.pointId,
        noteId: note.id,
        pointCode: search.pointCode,
        sampleId: search.sampleId,
        lng: search.lng,
        lat: search.lat,
        zoom: search.zoom,
        scope: search.scope,
      },
    });
  };

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Content className={classes.content}>
        <Stack gap={0} h="100%">
          <header className={classes.header}>
            <Text className={classes.headerTitle}>{t('sampling.notes.title')}</Text>
            <div className={classes.headerActions}>
              <ActionIcon
                aria-label={t('sampling.notes.createAria')}
                color="dark"
                onClick={handleCreateNote}
                radius="xl"
                size={32}
                variant="light"
              >
                <Plus size={18} />
              </ActionIcon>
              <ActionIcon
                aria-label={t('sampling.notes.closeAria')}
                color="dark"
                onClick={handleClose}
                radius="xl"
                size={32}
                variant="light"
              >
                <XIcon size={18} />
              </ActionIcon>
            </div>
          </header>

          <ScrollArea h="100%">
            <Stack className={classes.notesStack} gap="md">
              {notesQuery.data?.length === 0 ? (
                <Paper className={classes.card} p="md" radius="lg" withBorder>
                  <Text c="dimmed" size="sm">
                    {t('sampling.notes.empty')}
                  </Text>
                </Paper>
              ) : null}

              {sortedNotes.map((note) => (
                <Paper
                  className={classes.card}
                  component="button"
                  key={note.id}
                  onClick={() => openDetail(note)}
                  p="md"
                  radius="lg"
                  withBorder
                >
                  <Stack gap={10}>
                    <div className={classes.titleRow}>
                      <div className={classes.titleWrap}>
                        <Text className={classes.noteTitle}>{note.title}</Text>
                        <Text className={classes.noteDate}>
                          {dayjs(note.updated_at).format('DD/MM/YYYY HH:mm')}
                        </Text>
                      </div>

                      <div className={classes.actions}>
                        <ActionIcon
                          aria-label={t('sampling.notes.editAria')}
                          className={classes.actionButton}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openDetail(note);
                          }}
                          radius="xl"
                          size={30}
                          variant="light"
                        >
                          <Edit2 size={16} />
                        </ActionIcon>
                        <ActionIcon
                          aria-label={t('sampling.notes.deleteAria')}
                          className={classes.actionButton}
                          color="red"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteNoteMutation.mutate(note.id);
                          }}
                          radius="xl"
                          size={30}
                          variant="light"
                        >
                          <Trash2 size={16} />
                        </ActionIcon>
                      </div>
                    </div>

                    <span className={classes.greenBar} />

                    <Text className={classes.contentPreview}>{note.content}</Text>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </ScrollArea>
        </Stack>
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

export default PointNotesPage;
